import React, { useEffect, useState } from "react";
import { BlogModal, BlogApiData } from "./index";
import {
  useGetSeoBlogsQuery,
  useUpdateBlogByPkMutation,
  useInsertBlogMutation,
  Order_By,
} from "../../../api/generated/graphql";


export default function BlogsManagements() {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "draft" | "published" | "archived"
  >("all");

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<any | undefined>(
    undefined
  );
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [saveError, setSaveError] = useState<string | null>(null);

  const limit = 10;

  // GraphQL hooks - Get all blogs with proper pagination
  const queryVariables = {
    where: {
      ...(debouncedSearchTerm && {
        _or: [
          { slug: { _ilike: `%${debouncedSearchTerm}%` } },
          { al_metadata: { _cast: { String: { _ilike: `%${debouncedSearchTerm}%` } } } },
        ],
      }),
      ...(statusFilter !== "all" && {
        status: { _eq: statusFilter as any }
      }),
    },
    order_by: [
      {
        updated_at:
          sortOrder === "desc"
            ? Order_By.DescNullsLast
            : Order_By.AscNullsLast,
      },
    ],
    offset: (currentPage - 1) * limit,
    limit: limit,
  };



  const { data, loading, error, refetch } = useGetSeoBlogsQuery({
    variables: queryVariables,
    errorPolicy: "all",
  });

  const [updateBlog, { loading: isUpdating }] = useUpdateBlogByPkMutation({
    errorPolicy: "all",
  });

  const [insertBlog, { loading: isInserting }] = useInsertBlogMutation({
    errorPolicy: "all",
  });

  // Initialize from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get("page");
    const searchParam = urlParams.get("search");
    const sortParam = urlParams.get("sort");
    const statusParam = urlParams.get("status");

    if (pageParam && !isNaN(parseInt(pageParam))) {
      setCurrentPage(parseInt(pageParam));
    }
    if (searchParam) {
      setSearchTerm(searchParam);
      setDebouncedSearchTerm(searchParam);
    }
    if (sortParam && (sortParam === "asc" || sortParam === "desc")) {
      setSortOrder(sortParam);
    }
    if (
      statusParam &&
      ["all", "draft", "published", "archived"].includes(statusParam)
    ) {
      setStatusFilter(statusParam as any);
    }
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 and update URL when parameters change
  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm) return; // Only when debounce is complete
    setCurrentPage(1);
    updateURL(1, debouncedSearchTerm, sortOrder, statusFilter);
  }, [debouncedSearchTerm, searchTerm, sortOrder, statusFilter]);

  // Update URL when parameters change
  const updateURL = (
    page: number,
    search: string,
    sort: string,
    status: string
  ) => {
    const params = new URLSearchParams();
    if (page > 1) params.set("page", page.toString());
    if (search) params.set("search", search);
    if (sort !== "desc") params.set("sort", sort);
    if (status !== "all") params.set("status", status);

    const newUrl =
      window.location.pathname +
      (params.toString() ? "?" + params.toString() : "");
    window.history.replaceState({}, "", newUrl);
  };

  // Process GraphQL data - GraphQL query already handles filtering and sorting
  const processedData = React.useMemo(() => {


    if (!data?.blog || data.blog.length === 0) {
      return [];
    }

    const processed = data.blog.map((entry: any) => {
      return {
        ...entry,
        // Extract title and shortTitle from al_sections.heroSection, fallback to al_metadata for backward compatibility
        title: entry.al_sections?.heroSection?.title || entry.al_metadata?.title || 'Untitled',
        shortTitle: entry.al_sections?.heroSection?.short_title || 'No short title',
        hero_header: entry.al_sections?.heroSection?.description || 'No description',
        author_name: entry.al_sections?.authorSection?.full_name || 'Unknown Author',
        updated_at: entry.updated_at,
        status: entry.status,
      };
    });

    return processed;
  }, [data]);

  const currentData = processedData.length > 0 ? processedData : [];

  const totalCount = data?.blog_aggregate?.aggregate?.count || currentData.length;
  const totalPages = Math.ceil(totalCount / limit);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;


  const handleSortToggle = () => {
    const newSortOrder = sortOrder === "desc" ? "asc" : "desc";
    setSortOrder(newSortOrder);
    setCurrentPage(1);
    updateURL(1, searchTerm, newSortOrder, statusFilter);
  };

  const handleCreateEntry = () => {
    setSelectedEntry(undefined);
    setModalMode("create");
    setIsModalOpen(true);
  };

  const handleEditEntry = (entry: any) => {
    setSelectedEntry(entry);
    setModalMode("edit");
    setIsModalOpen(true);
  };

  const handleSaveEntry = async (data: {
    id?: string;
    blogData: BlogApiData;
    mode: "create" | "edit";
  }) => {
    try {
      setSaveError(null);
      const { id, blogData, mode } = data;

      const apiData: BlogApiData = {
        slug: blogData.slug,
        status: blogData.status,
        al_metadata: blogData.al_metadata,
        al_sections: blogData.al_sections
      };

      if (mode === "create") {
        // Transform BlogApiData to match GraphQL insert input structure
        const insertData = {
          slug: apiData.slug,
          status: apiData.status,
          al_metadata: apiData.al_metadata,
          al_sections: apiData.al_sections,
        };

        const insertVariables = {
          objects: [insertData],
        };

        const { data: insertResult, errors } = await insertBlog({
          variables: insertVariables,
        });

        if (errors) {
          console.error("GraphQL insert errors:", errors);
          throw new Error(errors[0].message);
        }

        if (insertResult?.insert_blog?.affected_rows && insertResult.insert_blog.affected_rows > 0) {
          await refetch();
        } else {
          console.error("=== GRAPHQL INSERT ERROR ===");
          console.error("No rows affected from insert mutation");
          console.error("insertResult:", insertResult);
          throw new Error("Failed to create blog entry - no rows affected");
        }

      } else if (mode === "edit" && id) {

        const updateVariables = {
          id,
          _set: {
            slug: apiData.slug,
            status: apiData.status as any,
            al_metadata: apiData.al_metadata,
            al_sections: apiData.al_sections,
          }
        };

        // console.log("=== UPDATE VARIABLES ===", updateVariables);


        const { data: updateResult, errors } = await updateBlog({
          variables: updateVariables
        });

        if (errors) {
          console.error("GraphQL errors:", errors);
          throw new Error(errors[0].message);
        }

        if (updateResult?.update_blog_by_pk) {

          await refetch();
        } else {
          console.error("=== GRAPHQL UPDATE ERROR ===");
          console.error("No data returned from update mutation");
          console.error("updateResult:", updateResult);
          throw new Error("Failed to update blog entry - no data returned");
        }
      }

      setIsModalOpen(false);
      setSelectedEntry(undefined);

    } catch (error) {
      console.error("Error saving blog entry:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save blog entry";
      setSaveError(errorMessage);
    }
  };

  const handleSearchChange = (search: string) => {
    setSearchTerm(search);
  };

  const handleStatusFilterChange = (
    status: "all" | "draft" | "published" | "archived"
  ) => {
    setStatusFilter(status);
    setCurrentPage(1);
    updateURL(1, searchTerm, sortOrder, status);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateURL(page, searchTerm, sortOrder, statusFilter);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const getStatusBadge = (status: "draft" | "published" | "archived") => {
    const statusConfig: Record<
      "draft" | "published" | "archived",
      { color: string; label: string }
    > = {
      draft: { color: "bg-yellow-100 text-yellow-800", label: "Draft" },
      published: { color: "bg-green-100 text-green-800", label: "Published" },
      archived: { color: "bg-gray-100 text-gray-800", label: "Archived" },
    };

    const config = statusConfig[status];
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Blog Management
          </h1>
          <p className="text-gray-600">
            Manage comprehensive blog content for Kaktu.al with real-time
            database integration
          </p>
        </div>

        {/* GraphQL Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-red-400 mr-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="text-red-800 font-medium">Error Loading Blog Data</h3>
                <p className="text-red-700 text-sm mt-1">
                  {error.message || "An unexpected error occurred while loading blog data."}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <button
                onClick={() => refetch()}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500">
                Retry Loading
              </button>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm border mb-6 p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              {/* Create New Blog Button */}
              <button
                onClick={handleCreateEntry}
                disabled={isUpdating || isInserting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {isUpdating || isInserting ? (
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                )}
                {isUpdating || isInserting ? "Processing..." : "Create New Blog"}
              </button>

              <div className="flex-1 max-w-md relative">
                <input
                  type="text"
                  placeholder="Search by title, header, or author..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="h-10 w-56 px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {searchTerm !== debouncedSearchTerm && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-blue-600"></div>
                  </div>
                )}
              </div>

              {/* Status Filter */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => handleStatusFilterChange("all")}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    statusFilter === "all"
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-800"
                  }`}>
                  All
                </button>
                <button
                  onClick={() => handleStatusFilterChange("published")}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    statusFilter === "published"
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-800"
                  }`}>
                  Published
                </button>
                <button
                  onClick={() => handleStatusFilterChange("draft")}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    statusFilter === "draft"
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-800"
                  }`}>
                  Draft
                </button>
                <button
                  onClick={() => handleStatusFilterChange("archived")}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    statusFilter === "archived"
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-800"
                  }`}>
                  Archived
                </button>
              </div>
            </div>

            {/* Results info */}
            <div className="text-sm text-gray-600">
              {loading
                ? "Loading blogs..."
                : `Showing ${(currentPage - 1) * limit + 1}-${Math.min(
                    currentPage * limit,
                    totalCount
                  )} of ${totalCount} entries`}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                    Blog Title
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                    Hero Header
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                    Author Name
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                    <button
                      onClick={handleSortToggle}
                      className="flex items-center gap-2 hover:text-blue-600 transition-colors focus:outline-none focus:text-blue-600 group">
                      <span>Last Updated</span>
                      <div className="flex flex-col">
                        {sortOrder === "desc" ? (
                          <svg
                            className="w-4 h-4 text-blue-600"
                            fill="currentColor"
                            viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4 text-blue-600"
                            fill="currentColor"
                            viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  // Loading skeleton rows
                  Array.from({ length: limit }, (_, index) => (
                    <tr key={`loading-${index}`} className="animate-pulse">
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-40"></div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-16"></div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-20"></div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-6 bg-gray-200 rounded w-12"></div>
                      </td>
                    </tr>
                  ))
                ) : currentData.length > 0 ? (
                  currentData.map((entry) => (
                    <tr
                      key={entry.id}
                      className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                          {entry.title}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {entry.hero_header}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">
                          {entry.author_name}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(entry.status)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(entry.updated_at).toLocaleString("sq-AL", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleEditEntry(entry)}
                          disabled={isUpdating || isInserting}
                          className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                          {isUpdating || isInserting ? "Processing..." : "Edit"}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        {debouncedSearchTerm || statusFilter !== "all"
                          ? `No blog entries found matching your criteria`
                          : "No blog entries found"}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!hasPrevPage}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
                  hasPrevPage
                    ? "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}>
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!hasNextPage}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
                  hasNextPage
                    ? "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}>
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">
                    {(currentPage - 1) * limit + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(currentPage * limit, totalCount)}
                  </span>{" "}
                  of <span className="font-medium">{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!hasPrevPage}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium ${
                      hasPrevPage
                        ? "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                        : "border-gray-200 bg-gray-100 text-gray-300 cursor-not-allowed"
                    }`}>
                    <span className="sr-only">Previous</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true">
                      <path
                        fillRule="evenodd"
                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {getPageNumbers().map((pageNum, index) => {
                    if (pageNum === "...") {
                      return (
                        <span
                          key={`ellipsis-${index}`}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      );
                    }

                    const isCurrentPage = pageNum === currentPage;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum as number)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          isCurrentPage
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}>
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNextPage}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium ${
                      hasNextPage
                        ? "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                        : "border-gray-200 bg-gray-100 text-gray-300 cursor-not-allowed"
                    }`}>
                    <span className="sr-only">Next</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true">
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {saveError && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <div className="flex items-center">
                <svg className="w-6 h-6 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900">Error Saving Blog</h3>
              </div>
              <p className="mt-2 text-sm text-gray-600">{saveError}</p>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setSaveError(null)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Blog Modal */}
        <BlogModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveEntry}
          entry={selectedEntry}
          mode={modalMode}
        />
      </div>
    </div>
  );
}
